import React from 'react';
import { FieldArray } from 'redux-form';
import PropTypes from 'prop-types';
import { is, path, uniq } from 'ramda';

import FormSection from 'containers/FormSectionContainer';
import FormField from 'containers/FormField';
import FieldSet from 'containers/FieldSetContainer';
import MedicationSearchContainer from '../containers/MedicationSearchContainer';
import ConditionalSection from 'containers/ConditionalSection';
import RadioButtonGroup from 'components/RadioButtonGroup';
import Datepicker from 'components/Datepicker';
import SelectField from 'components/SelectField';

import FormProvider from 'routes/Documents/containers/FormProvider';

import { fieldIs } from 'lib/conditions';

import MedicationEvents from './MedicationEvents';
import StrengthSelection from './StrengthSelection';
import { get } from 'lib/communication';

const amounts = [
  { value: '1', label: 'medication.amount.1' },
  { value: '2', label: 'medication.amount.2' },
  { value: '3', label: 'medication.amount.3' },
  { value: '4', label: 'medication.amount.4' },
  { value: '5', label: 'medication.amount.5' },
  { value: '6', label: 'medication.amount.6' },
  { value: '7', label: 'medication.amount.7' },
  { value: '8', label: 'medication.amount.8' },
];

const intervals = [
  { value: '1', label: 'medication.interval.1' },
  { value: '2', label: 'medication.interval.2' },
  { value: '3', label: 'medication.interval.3' },
  { value: '4', label: 'medication.interval.4' },
  { value: '5', label: 'medication.interval.5' },
  { value: '6', label: 'medication.interval.6' },
  { value: '7', label: 'medication.interval.7' },
  { value: '8', label: 'medication.interval.8' },
];

const formatStrengths = (strengths = []) => {
  const strengthsOptionsFormatted = strengths.map(s => {
    const strengthObject = path(['strengths'], s);
    const thisStrength = is(Object, strengthObject)
      ? { value: Object.values(strengthObject).join('/'), key: Object.values(strengthObject).join('/') }
      : null;
    return thisStrength.value ? thisStrength : null;
  });
  const strengthsOptionsFiltered = uniq(strengthsOptionsFormatted.filter(s => s));
  return strengthsOptionsFiltered;
};

const MedicationForm = props => {
  const [strengths, setStrengths] = React.useState([]);

  const medicationName = path(['formValues', 'drug', 'name'], props);
  const substances = path(['formValues', 'drug', 'substances'], props);

  React.useEffect(() => {
    if (medicationName) {
      const query = JSON.stringify({
        name: medicationName,
        substanceString: substances,
      });
      setStrengths([]); // Set this to empty or else the strengths options wont update
      get(`/medication/medication?medication=${query}`).then(data => {
        Array.isArray(data) && setStrengths(data);
      });
    }
  }, [medicationName]);

  const strengthsFormatted = formatStrengths(strengths);
  return (
    <FormProvider {...props}>
      <FormSection localeId="medication.sections.drug">
        <FieldSet localeId="medication.labels.chooseMedication">
          <FormField name="drug" component={MedicationSearchContainer} />
        </FieldSet>
      </FormSection>
      {medicationName && strengthsFormatted.length > 0 ? (
        <FormSection localeId="medication.sections.strength">
          <FieldSet localeId="medication.labels.chooseStrength">
            <FormField name="drug" component={StrengthSelection} strengths={strengthsFormatted} />
          </FieldSet>
        </FormSection>
      ) : null}
      <FormSection localeId="medication.sections.usageType">
        <FieldSet localeId="medication.labels.usageType">
          <FormField
            name="usageType"
            component={RadioButtonGroup}
            options={[
              { value: 'regular', label: 'medication.opts.regular' },
              { value: 'asNeeded', label: 'medication.opts.asNeeded' },
            ]}
          />
        </FieldSet>
        <ConditionalSection condition={fieldIs('usageType', 'regular')}>
          <FieldSet localeId="medication.labels.amount">
            <FormField name="amount" component={SelectField} hintLocale="medication.hints.amount" options={amounts} />
          </FieldSet>
          <FieldSet localeId="medication.labels.interval">
            <FormField
              name="interval"
              component={SelectField}
              hintLocale="medication.hints.interval"
              options={intervals}
            />
          </FieldSet>
          <FieldSet>
            <FormField
              name="intervalType"
              component={SelectField}
              hintLocale="medication.hints.intervalType"
              options={[
                { value: 'daily', label: 'medication.interval.daily' },
                { value: 'weekly', label: 'medication.interval.weekly' },
                { value: 'monthly', label: 'medication.interval.monthly' },
                { value: 'yearly', label: 'medication.interval.yearly' },
              ]}
            />
          </FieldSet>
        </ConditionalSection>
      </FormSection>
      <FormSection localeId="medication.sections.startAndEndDate">
        <FieldSet localeId="medication.labels.start">
          <FormField name="start" component={Datepicker} />
        </FieldSet>
        <FieldSet localeId="medication.labels.medicationHasEnded">
          <FormField name="hasEnded" component={RadioButtonGroup} preset="yesno" />
        </FieldSet>
        <ConditionalSection condition={fieldIs('hasEnded', 'yes')}>
          <FieldSet localeId="medication.labels.end">
            <FormField name="end" component={Datepicker} dateFloorHook="start" />
          </FieldSet>
        </ConditionalSection>
      </FormSection>
      <FormSection localeId="medication.sections.eventsDuringUse">
        {props.editing && <FieldArray name="events" component={MedicationEvents} />}
        {!props.editing && <MedicationEvents fields={props.formValues.events} view />}
      </FormSection>
    </FormProvider>
  );
};

export default MedicationForm;

MedicationForm.propTypes = {
  editing: PropTypes.bool,
  formValues: PropTypes.object,
};
